import { KatIcon } from "@amzn/katal-react";
import React from "react";
import { useTranslation } from "react-i18next";
import Markdown from "markdown-to-jsx";
import type {
  LocalizedText,
  NestStyle,
  NestUseCase,
  NestUseCaseDocumentationCommonErrors,
} from "src/api/interfaces/nest";
import "./index.css";

const NEST_STYLE_COLORS: Record<NestStyle, string> = {
  RED: "var(--kat-red-500)",
  YELLOW: "var(--kat-yellow-400)",
  ORANGE: "var(--kat-orange-400)",
  BLUE: "var(--kat-blue-500)",
};

interface NestExpectationsPanelProps {
  useCase: NestUseCase;
  documentTypeName: LocalizedText;
}

export function NestExpectationsPanel({
  useCase,
  documentTypeName,
}: NestExpectationsPanelProps) {
  const { t } = useTranslation();
  const { documentation, name } = useCase;
  const resolvedName = t(name.key, name.parameters);
  const resolvedFormat = t(documentTypeName.key, documentTypeName.parameters);

  return (
    <section
      className="nest-expectations-panel"
      aria-label={t("nest_expectations_title", { name: resolvedName })}
    >
      <h2 className="nest-expectations-header">
        {t("nest_expectations_title", { name: resolvedName })}
      </h2>

      <div className="nest-expectations-content">
        <div className="nest-expectations-format-banner">
          <strong>
            {t("nest_expectations_format_label", { format: resolvedFormat })}
          </strong>
          <Markdown>{t("nest_expectations_format_description")}</Markdown>
        </div>

        <div>
          <h3 className="nest-expectations-section-title">
            <KatIcon
              name="check_circle"
              size="small"
              className="nest-expectations-icon nest-expectations-icon--check"
            />
            {t("nest_expectations_what_we_expect")}
          </h3>
          <Markdown>
            {t(documentation.content.key, documentation.content.parameters)}
          </Markdown>
        </div>

        {documentation.commonErrors.length > 0 && (
          <div>
            <h3 className="nest-expectations-section-title">
              <KatIcon
                name="warning"
                size="small"
                className="nest-expectations-icon nest-expectations-icon--warning"
              />
              {t("nest_expectations_common_errors")}
            </h3>
            {documentation.commonErrors.map((error, i) => (
              <ErrorCard key={i} error={error} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ErrorCard({ error }: { error: NestUseCaseDocumentationCommonErrors }) {
  const { t } = useTranslation();

  return (
    <div
      className="nest-expectations-error-card"
      style={{ borderLeftColor: NEST_STYLE_COLORS[error.style] }}
    >
      <Markdown>{t(error.content.key, error.content.parameters)}</Markdown>
      {error.tags.length > 0 && (
        <div className="nest-expectations-tags">
          {error.tags.map((tag, j) => (
            <span
              key={j}
              className="nest-expectations-tag"
              style={{ backgroundColor: NEST_STYLE_COLORS[tag.style] }}
            >
              {t(tag.name.key, tag.name.parameters)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
